import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';
import gsap from 'gsap';

export type MobileNavPanel = 'main' | 'language' | 'admin' | 'settings';

export type MobileNavVariant = 'landing' | 'app';

const EASE_OUT = 'power3.out';
const EASE_IN = 'power2.in';
const MENU_DURATION = 0.42;
const ITEMS_DURATION = 0.4;
const ITEMS_STAGGER = 0.045;
const ITEMS_DELAY = 0.1;
const ITEMS_OFFSET_Y = 18;
const HEADER_BORDER_OPEN = '#1f1f1f';
const HEADER_BG_OPEN = '#000000';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function queryAnimTargets(root: HTMLElement | null) {
  if (!root) return [];
  return gsap.utils.toArray<HTMLElement>('[data-mobile-nav-item], [data-mobile-nav-block]');
}

function getHeaderChromeFrom(variant: MobileNavVariant, landingScrolled: boolean) {
  if (variant === 'landing') {
    if (landingScrolled) {
      return { backgroundColor: 'rgba(0, 0, 0, 0.5)', borderBottomColor: 'rgba(255, 255, 255, 0.15)' };
    }
    return { backgroundColor: 'rgba(0, 0, 0, 0)', borderBottomColor: 'rgba(255, 255, 255, 0.1)' };
  }
  return { backgroundColor: 'rgba(10, 10, 10, 0.9)', borderBottomColor: 'rgba(31, 31, 31, 0.85)' };
}

type UseMobileNavGsapOptions = {
  open: boolean;
  mounted: boolean;
  panel: MobileNavPanel;
  variant: MobileNavVariant;
  landingScrolled?: boolean;
  currentLang?: string;
  setPanel: (panel: MobileNavPanel) => void;
  shellRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  headerRef: RefObject<HTMLElement | null>;
  onCloseComplete: () => void;
};

/**
 * Mobile nav drawer: header + panel share one GSAP timeline on open/close.
 * Respects `prefers-reduced-motion`.
 */
export function useMobileNavGsap({
  open,
  mounted,
  panel,
  variant,
  landingScrolled = false,
  currentLang,
  setPanel,
  shellRef,
  contentRef,
  headerRef,
  onCloseComplete,
}: UseMobileNavGsapOptions) {
  const panelRef = useRef(panel);
  const prevPanelRef = useRef(panel);
  const panelTweenRef = useRef<gsap.core.Timeline | null>(null);
  const reducedRef = useRef(prefersReducedMotion());
  const landingScrolledRef = useRef(landingScrolled);

  useLayoutEffect(() => {
    landingScrolledRef.current = landingScrolled;
  }, [landingScrolled]);

  const navigatePanel = useCallback(
    (next: MobileNavPanel, direction: 'forward' | 'back') => {
      if (next === panelRef.current) return;

      if (reducedRef.current) {
        setPanel(next);
        panelRef.current = next;
        return;
      }

      panelTweenRef.current?.kill();
      const targets = queryAnimTargets(contentRef.current);
      const exitX = direction === 'forward' ? -28 : 28;
      const enterX = direction === 'forward' ? 28 : -28;

      const tl = gsap.timeline({
        onComplete: () => {
          setPanel(next);
          panelRef.current = next;
          requestAnimationFrame(() => {
            const incoming = queryAnimTargets(contentRef.current);
            if (!incoming.length) return;
            gsap.fromTo(
              incoming,
              { opacity: 0, x: enterX },
              {
                opacity: 1,
                x: 0,
                duration: 0.38,
                stagger: 0.04,
                ease: EASE_OUT,
                clearProps: 'opacity,x',
              },
            );
          });
        },
      });

      if (targets.length) {
        tl.to(targets, {
          opacity: 0,
          x: exitX,
          duration: 0.2,
          stagger: 0.02,
          ease: EASE_IN,
        });
      } else {
        tl.call(() => {
          setPanel(next);
          panelRef.current = next;
        });
      }

      panelTweenRef.current = tl;
    },
    [contentRef, setPanel],
  );

  useLayoutEffect(() => {
    panelRef.current = panel;
  }, [panel]);

  useLayoutEffect(() => {
    if (!open) prevPanelRef.current = 'main';
  }, [open]);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    const header = headerRef.current;
    if (!shell || !mounted) return;

    panelTweenRef.current?.kill();

    if (reducedRef.current) {
      gsap.set(shell, { clearProps: 'all' });
      if (header) {
        if (open) {
          gsap.set(header, {
            backgroundColor: HEADER_BG_OPEN,
            borderBottomColor: HEADER_BORDER_OPEN,
            backdropFilter: 'none',
          });
        } else {
          gsap.set(header, { clearProps: 'backgroundColor,borderBottomColor,backdropFilter' });
        }
      }
      if (!open) onCloseComplete();
      return;
    }

    const targets = queryAnimTargets(contentRef.current);
    const headerFrom = getHeaderChromeFrom(variant, landingScrolledRef.current);

    if (open) {
      gsap.set(shell, { opacity: 0, y: ITEMS_OFFSET_Y, clearProps: 'xPercent' });
      if (targets.length) gsap.set(targets, { opacity: 0, y: ITEMS_OFFSET_Y });

      const tl = gsap.timeline();
      if (header) {
        tl.fromTo(
          header,
          headerFrom,
          {
            backgroundColor: HEADER_BG_OPEN,
            borderBottomColor: HEADER_BORDER_OPEN,
            backdropFilter: 'none',
            duration: MENU_DURATION,
            ease: EASE_OUT,
          },
          0,
        );
      }
      tl.to(shell, { opacity: 1, y: 0, duration: MENU_DURATION, ease: EASE_OUT }, 0);
      if (targets.length) {
        tl.to(
          targets,
          {
            opacity: 1,
            y: 0,
            duration: ITEMS_DURATION,
            stagger: ITEMS_STAGGER,
            ease: EASE_OUT,
            clearProps: 'opacity,y',
          },
          ITEMS_DELAY,
        );
      }

      return () => {
        tl.kill();
      };
    }

    // Close: exact reverse of open — items out first, then panel fades/slides down with header.
    gsap.set(shell, { opacity: 1, y: 0, clearProps: 'xPercent' });

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set(shell, { clearProps: 'all' });
        if (header) gsap.set(header, { clearProps: 'backgroundColor,borderBottomColor,backdropFilter' });
        onCloseComplete();
      },
    });

    if (targets.length) {
      tl.to(
        targets,
        {
          opacity: 0,
          y: ITEMS_OFFSET_Y,
          duration: ITEMS_DURATION,
          stagger: { each: ITEMS_STAGGER, from: 'end' },
          ease: EASE_IN,
        },
        0,
      );
    }

    const chromeStart = targets.length ? ITEMS_DELAY : 0;
    tl.to(shell, { opacity: 0, y: ITEMS_OFFSET_Y, duration: MENU_DURATION, ease: EASE_IN }, chromeStart);
    if (header) {
      tl.to(
        header,
        {
          ...headerFrom,
          duration: MENU_DURATION,
          ease: EASE_IN,
        },
        chromeStart,
      );
    }

    return () => {
      tl.kill();
    };
  }, [open, mounted, variant, shellRef, contentRef, headerRef, onCloseComplete]);

  useLayoutEffect(() => {
    if (!open || !mounted || reducedRef.current) return;
    if (prevPanelRef.current === panel) return;
    prevPanelRef.current = panel;
  }, [panel, open, mounted]);

  useLayoutEffect(() => {
    if (!open || !mounted || reducedRef.current || panel !== 'language') return;
    const checks = contentRef.current?.querySelectorAll<HTMLElement>('[data-mobile-nav-check]');
    if (!checks?.length) return;

    gsap.fromTo(
      checks,
      { scale: 0, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.35, ease: 'back.out(2.2)', clearProps: 'scale,opacity' },
    );
  }, [panel, open, mounted, contentRef, currentLang]);

  return { navigatePanel };
}
